import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  Box,
  Typography,
  Avatar,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Snackbar,
  Alert,
  InputBase,
} from "@mui/material";
import CallIcon from "@mui/icons-material/Call";
import ShareIcon from "@mui/icons-material/Share";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import PlaceIcon from "@mui/icons-material/Place";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PhoneIcon from "@mui/icons-material/Phone";
import SearchIcon from "@mui/icons-material/Search";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import SecurityIcon from "@mui/icons-material/Security";
import { uploadLink } from "../../utils/constants";
import { getWhatsAppUrl } from "../../utils/whatsappHelper";
import { StoreThemeTokens, getStoreTheme } from "./themes/storeThemes";
import { CategoryItem, getStoreCategories } from "../../utils/categoryHelper";
import { useNavigate } from "react-router-dom";
import { getWarrantyPageUrl } from "../../utils/warrantyHelper";

interface StoreHeaderProps {
  storeData: any;
  themeTokens?: StoreThemeTokens;
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
  selectedCategory?: string;
  onSelectCategory?: (category: string) => void;
  categories?: CategoryItem[];
}

const StoreHeader: React.FC<StoreHeaderProps> = ({
  storeData,
  themeTokens: propTokens,
  searchQuery = "",
  onSearchChange,
  selectedCategory = "All",
  onSelectCategory,
  categories: categoriesProp,
}) => {
  const navigate = useNavigate();
  const [moreDialogOpen, setMoreDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "info" | "error">("success");

  const categoriesList = categoriesProp || getStoreCategories(storeData);
  const enabledCategories = categoriesList.filter((c) => c.enabled !== false);

  const themeTokens = propTokens || getStoreTheme(storeData?.theme);
  const header = storeData?.header || {};

  // Backward-compatible field fallbacks
  const logo = header.logo || "";
  const storeName = header.store_name || storeData?.name || "RUDRIX";
  const description = header.description || storeData?.description || "";
  const tagline = header.tagline || "Elegance Meets Care";
  const phoneNumber = header.phone_number || (storeData?.number ? String(storeData.number) : "");
  const location = header.location || storeData?.address || "Thane, India";

  // Toggles
  const showLogo = header.show_logo !== undefined ? Boolean(header.show_logo) : true;
  const showStoreName = header.show_store_name !== undefined ? Boolean(header.show_store_name) : true;
  const showLocation = header.show_location !== undefined ? Boolean(header.show_location) : true;
  const showCallButton = header.show_call_button !== undefined ? Boolean(header.show_call_button) : true;
  const showShareButton = header.show_share_button !== undefined ? Boolean(header.show_share_button) : true;
  const showMoreButton = header.show_more_button !== undefined ? Boolean(header.show_more_button) : true;

  // Customizable button labels
  const callButtonLabel = header.call_button_label || "Call";
  const shareButtonLabel = header.share_button_label || "Share";
  const moreButtonLabel = header.more_button_label || "More";

  const getLogoUrl = (path: string): string => {
    if (!path) return "";
    if (path.startsWith("http://") || path.startsWith("https://") || path.startsWith("data:")) {
      return path;
    }
    const cleanPath = path.startsWith("/") ? path.slice(1) : path;
    return `${uploadLink}${cleanPath}`;
  };

  const logoSrc = getLogoUrl(logo);

  const handleCall = () => {
    if (phoneNumber) {
      window.location.href = `tel:${phoneNumber}`;
    } else {
      setSnackbarMessage("Phone number not available.");
      setSnackbarSeverity("info");
      setSnackbarOpen(true);
    }
  };

  const handleWhatsAppClick = () => {
    if (phoneNumber) {
      const waUrl = getWhatsAppUrl(
        phoneNumber,
        `Hi ${storeName}, I'm visiting your store`
      );
      if (waUrl) window.open(waUrl, "_blank");
    } else {
      setSnackbarMessage("WhatsApp number not available.");
      setSnackbarSeverity("info");
      setSnackbarOpen(true);
    }
  };

  const handleCopyLink = () => {
    const url = window.location.href;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(url)
        .then(() => {
          setSnackbarMessage("Store link copied to clipboard!");
          setSnackbarSeverity("success");
          setSnackbarOpen(true);
        })
        .catch(() => fallbackClipboardCopy(url));
    } else {
      fallbackClipboardCopy(url);
    }
  };

  const fallbackClipboardCopy = (text: string) => {
    try {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.left = "-9999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setSnackbarMessage("Store link copied to clipboard!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch {
      setSnackbarMessage("Failed to copy link. Please copy URL directly.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleShare = async () => {
    const shareData = {
      title: storeName,
      text: tagline ? `${storeName} - ${tagline}` : `Check out ${storeName}`,
      url: window.location.href,
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err: any) {
        if (err.name !== "AbortError") {
          handleCopyLink();
        }
      }
    } else {
      handleCopyLink();
    }
  };

  const hasActionButtons =
    (showCallButton && Boolean(phoneNumber)) || showShareButton || showMoreButton;

  const getInitials = (name: string) => {
    if (!name) return "R";
    const parts = name.trim().split(" ");
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  const handleCategoryClick = (cat: string) => {
    if (onSelectCategory) {
      onSelectCategory(cat === "All Products" ? "All" : cat);
    }
    const section = document.getElementById("products-section");
    if (section) {
      section.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: themeTokens.bgHeader,
          backdropFilter: "blur(12px)",
          color: themeTokens.textPrimary,
          borderBottom: `1px solid ${themeTokens.bgHeaderBorder}`,
          boxShadow: "0 2px 12px rgba(0,0,0,0.03)",
          width: "100%",
          boxSizing: "border-box",
          zIndex: 50,
        }}
      >
        <Toolbar
          disableGutters
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            px: { xs: 1.5, sm: 2.5, md: 4 },
            py: { xs: 1, sm: 1.25 },
            gap: { xs: 1, sm: 2, md: 3 },
            minHeight: { xs: 58, md: 68 },
            maxWidth: 1280,
            mx: "auto",
            width: "100%",
            boxSizing: "border-box",
          }}
        >
          {/* Left: Brand Logo & Title */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 1.25, sm: 1.75 },
              minWidth: 0,
              flexShrink: 0,
            }}
          >
            {showLogo && (
              <Box sx={{ flexShrink: 0 }}>
                {logoSrc ? (
                  <Box
                    sx={{
                      width: { xs: 44, sm: 50, md: 54 },
                      height: { xs: 44, sm: 50, md: 54 },
                      borderRadius: "50%",
                      overflow: "hidden",
                      border: `1.5px solid ${themeTokens.accent}`,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                      bgcolor: "#ffffff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <img
                      src={logoSrc}
                      alt={storeName}
                      crossOrigin="anonymous"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  </Box>
                ) : (
                  <Avatar
                    sx={{
                      width: { xs: 44, sm: 50, md: 54 },
                      height: { xs: 44, sm: 50, md: 54 },
                      borderRadius: "50%",
                      bgcolor: themeTokens.accent,
                      color: "#ffffff",
                      fontWeight: 700,
                      fontSize: { xs: "1rem", sm: "1.15rem" },
                      boxShadow: `0 2px 8px ${themeTokens.accent}40`,
                    }}
                  >
                    {getInitials(storeName)}
                  </Avatar>
                )}
              </Box>
            )}

            <Box sx={{ minWidth: 0 }}>
              {showStoreName && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <Typography
                    variant="h5"
                    component="h1"
                    sx={{
                      fontFamily: themeTokens.fontSerif,
                      fontWeight: 700,
                      color: themeTokens.textPrimary,
                      fontSize: { xs: "1.2rem", sm: "1.45rem", md: "1.7rem" },
                      letterSpacing: "0.05em",
                      textTransform: "uppercase",
                      lineHeight: 1.15,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={storeName}
                  >
                    {storeName}
                  </Typography>
                  <Tooltip title="Verified Brand" arrow>
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        borderRadius: "50%",
                        bgcolor: "#10b981",
                        color: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "10px",
                        fontWeight: "bold",
                        flexShrink: 0,
                      }}
                    >
                      ✓
                    </Box>
                  </Tooltip>
                </Box>
              )}

              {tagline && (
                <Typography
                  variant="caption"
                  sx={{
                    color: themeTokens.textSecondary,
                    fontWeight: 600,
                    fontSize: { xs: "0.68rem", sm: "0.75rem" },
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                    display: "block",
                    lineHeight: 1.2,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  title={tagline}
                >
                  {tagline}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Middle: Store Search Bar (Desktop / Tablet) */}
          <Box
            sx={{
              flex: 1,
              maxWidth: 480,
              mx: { sm: 2, md: 4 },
              display: { xs: "none", md: "block" },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                bgcolor: "#FFFFFF",
                borderRadius: 999,
                border: `1px solid ${themeTokens.cardBorder}`,
                px: 2,
                py: 0.6,
                boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                "&:focus-within": {
                  borderColor: themeTokens.accent,
                  boxShadow: `0 0 0 2px ${themeTokens.accent}25`,
                },
              }}
            >
              <SearchIcon sx={{ color: themeTokens.textSecondary, fontSize: 20, mr: 1 }} />
              <InputBase
                fullWidth
                placeholder={`Search in ${storeName} (e.g. hair styler, massage belt...)`}
                value={searchQuery}
                onChange={(e) => onSearchChange && onSearchChange(e.target.value)}
                sx={{
                  fontSize: "0.85rem",
                  color: themeTokens.textPrimary,
                  "& input::placeholder": {
                    color: themeTokens.textSecondary,
                    opacity: 0.8,
                  },
                }}
              />
            </Box>
          </Box>

          {/* Right: Quick Actions & Buttons */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 1, sm: 1.5, md: 2 },
              flexShrink: 0,
            }}
          >
            {/* WhatsApp Quick Contact Pill Button (Hidden on smallest mobile, visible sm+) */}
            {phoneNumber && (
              <Button
                variant="outlined"
                onClick={handleWhatsAppClick}
                startIcon={<WhatsAppIcon sx={{ fontSize: 18 }} />}
                sx={{
                  display: { xs: "none", sm: "inline-flex" },
                  borderRadius: 999,
                  bgcolor: "#f0fdf4",
                  color: "#16a34a",
                  borderColor: "#bbf7d0",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.78rem",
                  px: 2,
                  py: 0.6,
                  "&:hover": {
                    bgcolor: "#dcfce7",
                    borderColor: "#86efac",
                  },
                }}
              >
                WhatsApp Us
              </Button>
            )}

            {/* Location (Desktop) */}
            {showLocation && location && (
              <Box
                sx={{
                  display: { xs: "none", xl: "flex" },
                  alignItems: "center",
                  gap: 0.5,
                  pl: 2,
                  borderLeft: `1px solid ${themeTokens.cardBorder}`,
                  color: themeTokens.textSecondary,
                }}
              >
                <PlaceIcon sx={{ fontSize: 16, color: themeTokens.accent }} />
                <Typography variant="caption" sx={{ fontWeight: 600, color: themeTokens.textPrimary }}>
                  {location}
                </Typography>
              </Box>
            )}

            {/* Circular Action Buttons (Call, Share, More) */}
            {hasActionButtons && (
              <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.75, sm: 1 } }}>
                {/* Call Button */}
                {showCallButton && Boolean(phoneNumber) && (
                  <Box
                    sx={{ display: "flex", flexDirection: "column", alignItems: "center", cursor: "pointer" }}
                    onClick={handleCall}
                  >
                    <Tooltip title={`${callButtonLabel}: ${phoneNumber}`} arrow>
                      <IconButton
                        aria-label={callButtonLabel}
                        sx={{
                          width: { xs: 36, sm: 40 },
                          height: { xs: 36, sm: 40 },
                          borderRadius: "50%",
                          bgcolor: themeTokens.accentLight,
                          color: themeTokens.accent,
                          border: `1px solid ${themeTokens.cardBorder}`,
                          "&:hover": {
                            bgcolor: themeTokens.accent,
                            color: "#ffffff",
                          },
                        }}
                      >
                        <CallIcon sx={{ fontSize: { xs: 17, sm: 19 } }} />
                      </IconButton>
                    </Tooltip>
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: "0.68rem",
                        fontWeight: 600,
                        color: themeTokens.textSecondary,
                        mt: 0.2,
                      }}
                    >
                      {callButtonLabel}
                    </Typography>
                  </Box>
                )}

                {/* Share Button */}
                {showShareButton && (
                  <Box
                    sx={{ display: "flex", flexDirection: "column", alignItems: "center", cursor: "pointer" }}
                    onClick={handleShare}
                  >
                    <Tooltip title={shareButtonLabel} arrow>
                      <IconButton
                        aria-label={shareButtonLabel}
                        sx={{
                          width: { xs: 36, sm: 40 },
                          height: { xs: 36, sm: 40 },
                          borderRadius: "50%",
                          bgcolor: "#FFFFFF",
                          color: themeTokens.textPrimary,
                          border: `1px solid ${themeTokens.cardBorder}`,
                          "&:hover": {
                            bgcolor: themeTokens.textPrimary,
                            color: "#ffffff",
                          },
                        }}
                      >
                        <ShareIcon sx={{ fontSize: { xs: 17, sm: 19 } }} />
                      </IconButton>
                    </Tooltip>
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: "0.68rem",
                        fontWeight: 600,
                        color: themeTokens.textSecondary,
                        mt: 0.2,
                      }}
                    >
                      {shareButtonLabel}
                    </Typography>
                  </Box>
                )}

                {/* More Button */}
                {showMoreButton && (
                  <Box
                    sx={{ display: "flex", flexDirection: "column", alignItems: "center", cursor: "pointer" }}
                    onClick={() => setMoreDialogOpen(true)}
                  >
                    <Tooltip title={moreButtonLabel} arrow>
                      <IconButton
                        aria-label={moreButtonLabel}
                        sx={{
                          width: { xs: 36, sm: 40 },
                          height: { xs: 36, sm: 40 },
                          borderRadius: "50%",
                          bgcolor: "#FFFFFF",
                          color: themeTokens.textPrimary,
                          border: `1px solid ${themeTokens.cardBorder}`,
                          "&:hover": {
                            bgcolor: themeTokens.textPrimary,
                            color: "#ffffff",
                          },
                        }}
                      >
                        <MoreHorizIcon sx={{ fontSize: { xs: 17, sm: 19 } }} />
                      </IconButton>
                    </Tooltip>
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: "0.68rem",
                        fontWeight: 600,
                        color: themeTokens.textSecondary,
                        mt: 0.2,
                      }}
                    >
                      {moreButtonLabel}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Toolbar>

        {/* Category Sub-navigation Bar */}
        {enabledCategories.length > 0 && (
          <Box
            component="nav"
            sx={{
              borderTop: `1px solid ${themeTokens.bgHeaderBorder}`,
              bgcolor: "rgba(255,255,255,0.7)",
              backdropFilter: "blur(8px)",
            }}
          >
            <Box
              sx={{
                maxWidth: 1280,
                mx: "auto",
                px: { xs: 1.5, sm: 2.5, md: 4 },
                display: "flex",
                alignItems: "center",
                gap: { xs: 2.5, sm: 3.5, md: 4.5 },
                py: 1,
                overflowX: "auto",
                "&::-webkit-scrollbar": { display: "none" },
                scrollbarWidth: "none",
              }}
            >
              {/* All Products */}
              <Typography
                onClick={() => handleCategoryClick("All Products")}
                variant="caption"
                sx={{
                  fontSize: "0.72rem",
                  fontWeight: selectedCategory === "All" || selectedCategory === "All Products" ? 700 : 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  color:
                    selectedCategory === "All" || selectedCategory === "All Products"
                      ? themeTokens.accent
                      : themeTokens.textSecondary,
                  borderBottom:
                    selectedCategory === "All" || selectedCategory === "All Products"
                      ? `2px solid ${themeTokens.accent}`
                      : "2px solid transparent",
                  pb: 0.25,
                  transition: "all 0.2s ease",
                  "&:hover": {
                    color: themeTokens.accent,
                  },
                }}
              >
                All Products
              </Typography>

              {/* Dynamic Enabled Categories */}
              {enabledCategories.map((cat) => {
                const active = selectedCategory === cat.name;
                const isSale =
                  Boolean(cat.isOffer) ||
                  cat.tag?.toUpperCase() === "SALE" ||
                  cat.name.toLowerCase().includes("special offer") ||
                  cat.name.toLowerCase().includes("sale");
                const badgeText = cat.tag || (isSale ? "SALE" : "");

                return (
                  <Typography
                    key={cat.id || cat.name}
                    onClick={() => handleCategoryClick(cat.name)}
                    variant="caption"
                    sx={{
                      fontSize: "0.72rem",
                      fontWeight: active ? 700 : 600,
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                      color: isSale
                        ? "#e11d48"
                        : active
                        ? themeTokens.accent
                        : themeTokens.textSecondary,
                      borderBottom: active ? `2px solid ${themeTokens.accent}` : "2px solid transparent",
                      pb: 0.25,
                      transition: "all 0.2s ease",
                      "&:hover": {
                        color: isSale ? "#be123c" : themeTokens.accent,
                      },
                    }}
                  >
                    {cat.name}
                    {badgeText && (
                      <Box
                        component="span"
                        sx={{
                          ml: 0.75,
                          px: 0.75,
                          py: 0.1,
                          bgcolor: isSale ? "#ffe4e6" : "#f1f5f9",
                          color: isSale ? "#e11d48" : "#475569",
                          borderRadius: 999,
                          fontSize: "0.62rem",
                          fontWeight: 700,
                        }}
                      >
                        {badgeText}
                      </Box>
                    )}
                  </Typography>
                );
              })}

              {/* Warranty Navigation Tab */}
              <Typography
                onClick={() => {
                  const el = document.getElementById("warranty-registration");
                  if (el) {
                    el.scrollIntoView({ behavior: "smooth" });
                  } else {
                    navigate(getWarrantyPageUrl(storeData?.url));
                  }
                }}
                variant="caption"
                sx={{
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  color: "#16a34a",
                  pb: 0.25,
                  borderBottom: "2px solid transparent",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    color: "#15803d",
                    borderBottom: "2px solid #16a34a",
                  },
                }}
              >
                <SecurityIcon sx={{ fontSize: 13 }} />
                Warranty
              </Typography>
            </Box>
          </Box>
        )}
      </AppBar>

      {/* More Info Dialog / Modal */}
      <Dialog
        open={moreDialogOpen}
        onClose={() => setMoreDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 1,
            boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            {logoSrc ? (
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  overflow: "hidden",
                  border: `1px solid ${themeTokens.accent}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <img
                  src={logoSrc}
                  alt={storeName}
                  crossOrigin="anonymous"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              </Box>
            ) : (
              <Avatar
                sx={{
                  width: 40,
                  height: 40,
                  bgcolor: themeTokens.accent,
                  fontSize: "0.9rem",
                  fontWeight: 700,
                }}
              >
                {getInitials(storeName)}
              </Avatar>
            )}
            <Box>
              <Typography variant="h6" sx={{ fontFamily: themeTokens.fontSerif, fontWeight: 700, fontSize: "1.2rem" }}>
                {storeName}
              </Typography>
              {tagline && (
                <Typography variant="caption" sx={{ color: themeTokens.accent, fontWeight: 600 }}>
                  {tagline}
                </Typography>
              )}
            </Box>
          </Box>
          <IconButton onClick={() => setMoreDialogOpen(false)} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ py: 2 }}>
          {description && (
            <Box sx={{ mb: 2.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: themeTokens.textPrimary, mb: 0.5 }}>
                About the Store
              </Typography>
              <Typography variant="body2" sx={{ color: themeTokens.textSecondary, lineHeight: 1.6 }}>
                {description}
              </Typography>
            </Box>
          )}

          {phoneNumber && (
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 2 }}>
              <PhoneIcon sx={{ color: themeTokens.accent, fontSize: 20, mt: 0.2 }} />
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: themeTokens.textPrimary }}>
                  Phone / WhatsApp
                </Typography>
                <Typography variant="body2" sx={{ color: themeTokens.textSecondary }}>
                  {phoneNumber}
                </Typography>
              </Box>
            </Box>
          )}

          {location && (
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 2 }}>
              <PlaceIcon sx={{ color: "#ef4444", fontSize: 20, mt: 0.2 }} />
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: themeTokens.textPrimary }}>
                  Store Location
                </Typography>
                <Typography variant="body2" sx={{ color: themeTokens.textSecondary }}>
                  {location}
                </Typography>
              </Box>
            </Box>
          )}

          {storeData?.operating_hours && (
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 2 }}>
              <AccessTimeIcon sx={{ color: "#059669", fontSize: 20, mt: 0.2 }} />
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: themeTokens.textPrimary }}>
                  Operating Hours
                </Typography>
                <Typography variant="body2" sx={{ color: themeTokens.textSecondary }}>
                  {storeData.operating_hours}
                </Typography>
              </Box>
            </Box>
          )}

          {storeData?.delivery && (
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 1 }}>
              <LocalShippingIcon sx={{ color: "#d97706", fontSize: 20, mt: 0.2 }} />
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: themeTokens.textPrimary }}>
                  Delivery Information
                </Typography>
                <Typography variant="body2" sx={{ color: themeTokens.textSecondary }}>
                  {storeData.delivery}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 2.5, py: 1.5, gap: 1, flexWrap: "wrap" }}>
          <Button
            variant="outlined"
            startIcon={<SecurityIcon />}
            onClick={() => {
              setMoreDialogOpen(false);
              const el = document.getElementById("warranty-registration");
              if (el) {
                el.scrollIntoView({ behavior: "smooth" });
              } else {
                navigate(getWarrantyPageUrl(storeData?.url));
              }
            }}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              borderColor: themeTokens.accent,
              color: themeTokens.accent,
              "&:hover": {
                borderColor: themeTokens.accentHover,
                bgcolor: `${themeTokens.accent}12`,
              },
            }}
          >
            Register Warranty
          </Button>
          <Button
            variant="outlined"
            startIcon={<ContentCopyIcon />}
            onClick={handleCopyLink}
            sx={{ textTransform: "none", borderRadius: 2 }}
          >
            Copy Store Link
          </Button>
          {phoneNumber && (
            <Button
              variant="contained"
              startIcon={<CallIcon />}
              onClick={handleCall}
              sx={{
                textTransform: "none",
                borderRadius: 2,
                bgcolor: themeTokens.accent,
                "&:hover": { bgcolor: themeTokens.accentHover },
              }}
            >
              {callButtonLabel}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Feedback Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%", boxShadow: "0 4px 12px rgba(0,0,0,0.15)" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default StoreHeader;
